import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const SEVERITY_CONFIG = {
  critical: {
    icon: ErrorOutlineIcon,
    bg: "hsl(0 70% 97%)",
    border: "hsl(0 70% 85%)",
    color: "#D32F2F",
    chipBg: "rgba(211,47,47,0.10)",
    label: "Critical",
  },
  warning: {
    icon: WarningAmberIcon,
    bg: "hsl(36 90% 97%)",
    border: "hsl(36 80% 80%)",
    color: "#F57C00",
    chipBg: "rgba(245,124,0,0.10)",
    label: "Warning",
  },
  info: {
    icon: TrendingUpIcon,
    bg: "hsl(142 50% 97%)",
    border: "hsl(142 40% 82%)",
    color: "#079344",
    chipBg: "rgba(7,147,68,0.10)",
    label: "Info",
  },
};

function AlertItem({ alert, index }) {
  const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderRadius: "0.5rem",
        border: `1px solid ${config.border}`,
        backgroundColor: config.bg,
      }}
    >
      <Icon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontWeight: 600,
            color: config.color,
            lineHeight: 1.4,
          }}
        >
          {alert.message}
        </Typography>
        {alert.detail && (
          <Typography
            sx={{
              fontSize: 11,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(150 10% 46%)",
              lineHeight: 1.3,
              mt: 0.25,
            }}
          >
            {alert.detail}
          </Typography>
        )}
      </Box>
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.chipBg,
          color: config.color,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 10,
          fontWeight: 600,
          height: 20,
          borderRadius: "6px",
          flexShrink: 0,
        }}
      />
    </Box>
  );
}

const ActivityAlertStrip = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;

  const sortedAlerts = [...alerts].sort((a, b) => {
    const order = { critical: 0, warning: 1, info: 2 };
    return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
  });

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      sx={{
        border: "1px solid hsl(140 8% 88%)",
        borderRadius: "0.75rem",
        backgroundColor: "#fff",
        p: 2,
      }}
    >
      <Typography
        sx={{
          color: "hsl(150 10% 46%)",
          fontSize: 13,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mb: 1.5,
        }}
      >
        Activity Alerts ({sortedAlerts.length})
      </Typography>
      <Stack spacing={1}>
        <AnimatePresence>
          {sortedAlerts.map((alert, i) => (
            <AlertItem key={alert.key} alert={alert} index={i} />
          ))}
        </AnimatePresence>
      </Stack>
    </Box>
  );
};

export default ActivityAlertStrip;
